import type {
  AssignmentPlan,
  AppState,
  BreakoutStage,
  LiveParticipantOption,
  LiveUnplacedRow,
  ManualPlacementDraft,
  OperatorRole,
  RoomParticipant,
  UnplacedPlanOption,
} from "../types.js";
import { escapeHtml } from "../security/escape-html.js";

/** Everything the preview needs about the live meeting to offer manual place/reassign controls. */
export interface MatchingContext {
  /** Live participants not currently claimed by any placement — offered for manual Place. */
  unclaimedLive: LiveParticipantOption[];
  /** Given an already-placed participant, the live identity currently assigned to them, if known. */
  currentLiveEntry: (p: RoomParticipant) => LiveParticipantOption | null;
  /** Stable identity key for a plan participant — see attendeeKey() in main.ts. */
  attendeeKey: (p: RoomParticipant) => string;
  /** VE-023 (Lite) + VE-026 (full events): live attendees not yet in a room, each with a confident guess if one exists. */
  liveUnplaced: LiveUnplacedRow[];
  /** VE-023 (Lite) + VE-026 (full events): plan participants still awaiting placement, offered in the manual search. */
  unplacedPlanOptions: UnplacedPlanOption[];
  /** VE-023 (Lite) + VE-026 (full events): plan participants no live attendee matched — the no-shows. */
  absentPlanParticipants: UnplacedPlanOption[];
  /** VE-026, full events only: present live attendees with no registration tie to this event. */
  unregisteredLive: LiveParticipantOption[];
  /** Late Zoom-email placements completed without interrupting the operator. */
  emailPlacementNotice?: string;
  /** Late Zoom-email conflicts for attendees whose rows are already placed. */
  emailConflictNotices?: string[];
  /**
   * How many people Zoom reports inside a room right now, or null when this
   * client cannot see into it (every room, for a co-host — Zoom returns room
   * occupancy to the meeting owner only). Null means "unknown", never "empty":
   * the caller falls back to the stored placements rather than printing 0.
   */
  occupancyOf: (roomName: string) => number | null;
}

/** Options for the Lite live-unplaced combobox — plan participants, each carrying their room. */
function planComboOptionsHtml(options: UnplacedPlanOption[]): string {
  return options
    .map(
      (o) => `<div class="combo-option"
        data-attendee-key="${escapeHtml(o.attendeeKey)}"
        data-room-key="${escapeHtml(o.roomKey)}"
        data-room-name="${escapeHtml(o.roomName)}"
        data-name="${escapeHtml(o.displayName.toLowerCase())}">${escapeHtml(o.displayName)}</div>`,
    )
    .join("");
}

/**
 * Combobox shell for the live-unplaced row (VE-023/VE-026):
 * the row is a live attendee, so the options are plan participants carrying
 * their intended room, and the field pre-fills with the app's suggestion
 * when one exists (AC-3), leaving it empty otherwise (AC-6).
 */
function planComboHtml(
  options: UnplacedPlanOption[],
  suggestion: LiveUnplacedRow["suggestion"],
  draft?: ManualPlacementDraft,
  disabled = false,
): string {
  const selected = draft?.attendeeKey
    ? options.find((option) => option.attendeeKey === draft.attendeeKey)
    : undefined;
  const initial = selected ?? suggestion;
  return `
    <div class="combo" data-place-combo>
      <input type="text" class="combo-input" data-place-search placeholder="Search who this is…" autocomplete="off"
             value="${escapeHtml(draft?.searchText ?? initial?.displayName ?? "")}"${disabled ? " disabled" : ""} />
      <input type="hidden" data-place-select value="${escapeHtml(draft?.attendeeKey || suggestion?.attendeeKey || "")}" />
      <input type="hidden" data-place-room-key value="${escapeHtml(draft?.roomKey || suggestion?.roomKey || "")}" />
      <div class="combo-list" data-place-list hidden>
        ${planComboOptionsHtml(options)}
      </div>
    </div>`;
}

const STEP_LABELS = ["Select round", "Create rooms", "Assign", "Open rooms", "Close rooms", "Next round"];

// Index of the step that is currently in progress (0-based) for each stage.
// Every step before this index is already done; "Select round" (index 0) is
// always done by the time an assignment plan exists.
const STAGE_TO_STEP_INDEX: Partial<Record<BreakoutStage, number>> = {
  idle: 1,
  created: 2,
  assigned: 3,
  opened: 4,
  closed: 5,
};

/**
 * Card copy per breakout stage, keyed off the same BreakoutStage the stepper
 * uses. Without this, "idle" / "created" / "assigned" all printed the same
 * "Assignment Plan — N rooms" heading, so an operator had no way to tell
 * whether Zoom had done anything yet — the stepper dot was the only signal,
 * and these three stages are invisible to attendees too (nobody moves until
 * "opened"). The title now names what actually exists: a proposal read from
 * the sheet, or real rooms in Zoom.
 */
interface StageCardMeta {
  cardTitle: string;
  chipLabel: string;
  chipClass: string;
  roomBlockClass: string;
  countLabel: (assigned: number, planned: number) => string;
  sentence: (assignedTotal: number, plannedTotal: number, roomCount: number) => string;
}

const STAGE_CARD_META: Partial<Record<BreakoutStage, StageCardMeta>> = {
  idle: {
    cardTitle: "Assignment plan",
    chipLabel: "Not created yet",
    chipClass: "is-planned",
    roomBlockClass: "is-planned",
    countLabel: (_assigned, planned) => `${planned} planned`,
    sentence: () => `Read from your sheet. <strong>Nothing exists in Zoom yet.</strong>`,
  },
  created: {
    cardTitle: "Breakout rooms",
    chipLabel: "Created · empty",
    chipClass: "is-empty",
    roomBlockClass: "is-empty",
    countLabel: (_assigned, planned) => `0 of ${planned}`,
    sentence: (_assignedTotal, _plannedTotal, roomCount) =>
      `<strong>${roomCount} room${roomCount !== 1 ? "s" : ""} now exist${roomCount === 1 ? "s" : ""} in Zoom.</strong> No one is assigned to them yet.`,
  },
  assigned: {
    cardTitle: "Breakout rooms",
    chipLabel: "Assigned · not moved",
    chipClass: "is-assigned",
    roomBlockClass: "is-assigned",
    countLabel: (assigned, planned) => `${assigned} of ${planned}`,
    sentence: (assignedTotal, plannedTotal) =>
      `<strong>${assignedTotal} of ${plannedTotal} placed.</strong> Everyone is still in the main room until you open.`,
  },
  opened: {
    cardTitle: "Breakout rooms",
    chipLabel: "Live · people inside",
    chipClass: "is-live",
    roomBlockClass: "is-assigned",
    countLabel: (assigned) => `${assigned} inside`,
    sentence: (assignedTotal) =>
      `<strong>${assignedTotal} participant${assignedTotal !== 1 ? "s are" : " is"} in breakout rooms right now.</strong>`,
  },
  closed: {
    cardTitle: "Breakout rooms",
    chipLabel: "Closed",
    chipClass: "is-done",
    roomBlockClass: "is-done",
    countLabel: (assigned) => `${assigned} were inside`,
    sentence: () => `Everyone is back in the main room. Moving on starts the next round's assignments.`,
  },
};

function renderStepper(state: AppState): string {
  const activeIndex = STAGE_TO_STEP_INDEX[state.breakoutStage];
  if (activeIndex === undefined) return "";

  const steps = STEP_LABELS.map((label, i) => {
    const cls = i < activeIndex ? "done" : i === activeIndex ? "active" : "";
    const mark = i < activeIndex ? "&#10003;" : String(i + 1);
    return `
      <div class="step ${cls}">
        <div class="dot">${mark}</div>
        <div class="step-label">${escapeHtml(label)}</div>
      </div>`;
  }).join("");

  return `<div class="card"><div class="stepper">${steps}</div></div>`;
}

export function renderAssignmentPreview(plan: AssignmentPlan, state: AppState, ctx: MatchingContext): string {
  const meta = STAGE_CARD_META[state.breakoutStage];

  // Who is inside an open room is knowable only to the meeting owner — Zoom
  // hands a co-host room names and nothing else. The stored placements are no
  // substitute: they belong to one assignment run, so a refresh answered with a
  // different run reports an empty room that plainly has people in it. Rather
  // than print a number this client cannot stand behind, print none — the
  // roster still shows who the round put in each room.
  const occupancyReadable = plan.rooms.some((room) => ctx.occupancyOf(room.room_name) !== null);
  const occupancyHidden = state.breakoutStage === "opened" && !occupancyReadable;

  let assignedTotal = 0;
  let plannedTotal = 0;

  const roomBlocks = plan.rooms
    .map((room, idx) => {
      const planned = room.participants.length;
      // zoom_participant_id lives on the plan itself and is persisted server-side
      // (publishStage's room_assignments), so it survives a refresh. Counting off
      // state.unmatchedAssignments instead used to work at Assign time but read
      // "fully placed" after any reload that reset that session-local list —
      // 1 were inside becoming 3 were inside purely from pressing Refresh.
      // Zoom's own occupancy wins whenever this client can read it: the stored
      // placements belong to one assignment run, and a refresh answered with a
      // different run reports nobody seated while people are visibly in the
      // room. Falls back to the plan only when Zoom will not say (co-host).
      const seated = room.participants.filter((p) => Boolean(p.zoom_participant_id)).length;
      const assigned = ctx.occupancyOf(room.room_name) ?? seated;
      plannedTotal += planned;
      assignedTotal += assigned;

      const countLabel = occupancyHidden
        ? ""
        : meta
        ? meta.countLabel(assigned, planned)
        : `${planned} participant${planned !== 1 ? "s" : ""}`;
      const blockClass = meta ? ` ${meta.roomBlockClass}` : "";

      const participants = room.participants
        .map((p) => {
          // Same rule per person: the dot is an occupancy claim, and a client
          // that cannot count the room cannot vouch for one attendee either.
          const isInRoom = !occupancyHidden && Boolean(p.zoom_participant_id);
          return `
          <div class="participant-row">
            <div>
              <div class="participant-name">${escapeHtml(p.email)}</div>
              <div class="participant-meta">
                ${p.display_name ? escapeHtml(p.display_name) : "Not yet synced"}
                ${isInRoom ? '<span class="in-room-indicator" title="In room">&#9679;</span>' : ""}
              </div>
            </div>
          </div>`;
        })
        .join("");

      return `
        <div class="room-block${blockClass}">
          <button type="button" class="room-label" data-room-toggle="${idx}">
            <span class="room-name">${escapeHtml(room.room_name)}</span>
            ${countLabel ? `<span class="room-count">${countLabel}</span>` : ""}
            <span class="chevron">&#9662;</span>
          </button>
          <div class="room-participants" data-room-panel="${idx}" hidden>
            ${participants}
          </div>
        </div>`;
    })
    .join("");

  // The stage sentence states the same count the pills do, so it has to go
  // quiet for the same reason. Says why the number is missing, so an operator
  // does not read a blank as a broken screen.
  const cardSub = occupancyHidden
    ? `<strong>The rooms are open.</strong> Only the meeting host's app can see who is inside, so no count is shown here.`
    : meta
    ? meta.sentence(assignedTotal, plannedTotal, plan.rooms.length)
    : "";

  const cardHead = meta
    ? `
      <div class="card-head">
        <div class="card-title">${meta.cardTitle}</div>
        <span class="stage-tag ${meta.chipClass}">${meta.chipClass === "is-live" ? '<span class="led"></span>' : ""}${meta.chipLabel}</span>
      </div>
      <div class="card-sub">${cardSub}</div>`
    : `<div class="card-title">Assignment Plan — ${plan.rooms.length} room${plan.rooms.length !== 1 ? "s" : ""}</div>`;

  return `
    ${renderStepper(state)}
    <div class="card">
      ${cardHead}
      <div class="scroll-window">
        ${roomBlocks}
      </div>
      ${plan.rooms.length === 0 ? '<div style="color:var(--app-gray);font-size:13px">No matches found for this round</div>' : ""}
    </div>
    ${renderUnmatchedSection(state, ctx)}`;
}

// Kept separate from renderAssignmentPreview so the caller can pin this part
// to the bottom of the viewport — with a long room/unmatched list the action
// button used to end up scrolled out of view below everything else.
export function renderAssignmentActions(
  plan: AssignmentPlan,
  state: AppState,
  /**
   * True when the error banner above already carries the host-only message.
   * Repeating it as a note under the stage line put the same sentence on screen
   * twice, in two different colours, which read as two separate problems.
   */
  hostNoteShownElsewhere = false,
): string {
  const { stageBanner, actionButton } = renderStageControls(plan, state, hostNoteShownElsewhere);
  return `${stageBanner}${actionButton}`;
}

/**
 * One banner per stage, never a stack.
 *
 * The stage line and the host-only advisory used to be two sibling alerts of
 * the same colour, so a co-host at "opened" saw three blue/red boxes in a row
 * before reaching a button. They are one box now: stage line first, advisory
 * beneath a hairline rule. An empty line with a note (e.g. "closed", where
 * the fact itself now lives in card-sub and only the host-only advisory is
 * left to say) renders the note as the whole alert rather than an empty row
 * plus a footnote under it.
 */
function stageNotice(line: string, note: string): string {
  if (!line) return note ? `<div class="alert alert-info">${note}</div>` : "";
  const noteHtml = note ? `<div class="alert-note">${note}</div>` : "";
  return `<div class="alert alert-info">${line}${noteHtml}</div>`;
}

function renderUnmatchedSection(state: AppState, ctx: MatchingContext): string {
  if (state.breakoutStage !== "assigned" && state.breakoutStage !== "opened") return "";
  // VE-023 gave Lite a live-attendee-driven list; VE-026 gives full events the
  // same list plus a third, informational section Lite has no equivalent for —
  // full events have a real registration signal (ParticipantSyncService) that
  // tells a walk-in apart from a registered participant, and Lite does not.
  const conflictNotices = (ctx.emailConflictNotices ?? [])
    .map((notice) => `<div class="alert alert-error">${escapeHtml(notice)}</div>`)
    .join("");
  const liveUnplaced = renderLiveUnplaced(ctx);
  if (state.breakoutStage === "opened") return `${conflictNotices}${liveUnplaced}`;
  return `${conflictNotices}${liveUnplaced}${renderAbsentParticipants(ctx)}${renderUnregisteredAttendees(ctx)}`;
}

/**
 * Lite: CSV participants no live attendee matched. Collapsed by default — it is
 * information, not an action, and an expanded list of no-shows would push the
 * place controls below the fold on a long round. Renders nothing when everyone
 * is accounted for, same as renderLiveUnplaced on an empty list.
 */
function renderAbsentParticipants(ctx: MatchingContext): string {
  const rows = ctx.absentPlanParticipants;
  if (rows.length === 0) return "";

  const rowsHtml = rows
    .map(
      (p) => `
      <div class="participant-row">
        <div>
          <div class="participant-name">${escapeHtml(p.displayName || p.email)}</div>
          <div class="participant-meta">${escapeHtml(p.email)} &middot; <span class="participant-meta-room">${escapeHtml(p.roomName)}</span></div>
        </div>
      </div>`,
    )
    .join("");

  return `
    <div class="card card-absent">
      <button type="button" class="absent-toggle" data-absent-toggle aria-expanded="false" aria-controls="absent-panel">
        <span class="card-title">Not in the meeting</span>
        <span class="stage-tag is-empty">${rows.length} missing</span>
        <span class="chevron">&#9662;</span>
      </button>
      <div class="scroll-window absent-panel" id="absent-panel" data-absent-panel hidden>
        ${rowsHtml}
      </div>
    </div>`;
}

/**
 * VE-026, full events only (always empty for Lite — ctx.unregisteredLive is
 * only ever populated in buildMatchingContext()'s full-event branch): people
 * physically in the meeting with no registration tie to this event at all.
 * Informational, not actionable — full-event rooms come from real registered-
 * user matches, so there is no room a stranger could ever belong in (AC-3).
 * No place control, unlike renderLiveUnplaced's rows.
 */
function renderUnregisteredAttendees(ctx: MatchingContext): string {
  const rows = ctx.unregisteredLive;
  if (rows.length === 0) return "";

  const rowsHtml = rows
    .map(
      (l) => `
      <div class="participant-row">
        <div>
          <div class="participant-name">${escapeHtml(l.screenName)}</div>
          <div class="participant-meta">In the meeting — not a registered participant of this event</div>
        </div>
      </div>`,
    )
    .join("");

  return `
    <div class="card card-absent">
      <button type="button" class="absent-toggle" data-unregistered-toggle aria-expanded="false" aria-controls="unregistered-panel">
        <span class="card-title">Not part of this event</span>
        <span class="stage-tag is-empty">${rows.length} unregistered</span>
        <span class="chevron">&#9662;</span>
      </button>
      <div class="scroll-window absent-panel" id="unregistered-panel" data-unregistered-panel hidden>
        ${rowsHtml}
      </div>
    </div>`;
}

/**
 * Lite (VE-023): one row per person physically in the meeting who still
 * needs a room. The row is the live attendee, so it can never claim someone
 * is absent when they are sitting in the meeting — the failure mode the
 * plan-driven list had, where an ambiguous name rendered as "Not found in
 * meeting" even though the person was present.
 */
function renderLiveUnplaced(ctx: MatchingContext): string {
  const rows = ctx.liveUnplaced;
  if (rows.length === 0 && !ctx.emailPlacementNotice) return "";
  if (rows.length === 0) {
    return `<div class="card"><div class="card-head"><div class="card-title">Need attention</div></div><div class="alert alert-info">${escapeHtml(ctx.emailPlacementNotice ?? "")}</div></div>`;
  }

  const rowsHtml = rows
    .map((r) => {
      const s = r.suggestion;
      const d = r.manualDraft;
      const pm = r.potentialMatch;
      const status = r.emailStatus
        ? `<div class="participant-meta ${r.emailStatus.kind === "confirmed" ? "email-match-confirmed" : "email-match-conflict"}">${escapeHtml(r.emailStatus.message)}</div>`
        : "";
      const error = r.placementError
        ? `<div class="participant-meta email-match-conflict">${escapeHtml(r.placementError)}</div>`
        : "";
      const potentialMatchHtml = pm && !d?.attendeeKey
        ? `<div class="participant-meta potential-match-hint">Potential match: ${escapeHtml(pm.displayName)} (${Math.round(pm.confidence * 100)}%) <button type="button" class="btn-link" data-accept-potential data-pm-attendee-key="${escapeHtml(pm.attendeeKey)}" data-pm-room-key="${escapeHtml(pm.roomKey)}" data-pm-display-name="${escapeHtml(pm.displayName)}" data-pm-room-name="${escapeHtml(pm.roomName)}">Accept</button></div>`
        : "";
      const roomName = d?.roomKey ? ctx.unplacedPlanOptions.find((option) => option.roomKey === d.roomKey)?.roomName : s?.roomName;
      return `
      <div class="participant-row">
        <div>
          <div class="participant-name">${escapeHtml(r.live.screenName)}</div>
          <div class="participant-meta">In the meeting — no room yet</div>
          ${status}
          ${error}
          ${potentialMatchHtml}
          <div class="participant-meta" data-placement-inflight${r.placementInFlight ? "" : " hidden"}>Placement in progress…</div>
        </div>
        <span class="match-badge unmatched">Needs a room</span>
      </div>
      <div class="manual-place manual-place-live" data-place-body data-live-uuid="${escapeHtml(r.live.uuid)}">
        ${planComboHtml(ctx.unplacedPlanOptions, s, d, r.placementInFlight)}
        <div class="place-confirm-row">
          <label class="place-batch-select"><input type="checkbox" data-place-batch-check${d?.selected ? " checked" : ""}${r.placementInFlight ? " disabled" : ""} /> Select</label>
          <span class="place-room-chip" data-place-room>${roomName ? escapeHtml(roomName) : ""}</span>
          <button type="button" class="btn-sm btn-primary" data-place-confirm${r.placementInFlight ? " disabled" : ""}>Place</button>
        </div>
      </div>`;
    })
    .join("");

  return `
    <div class="card">
      <div class="card-head">
        <div class="card-title">Need attention</div>
        <span class="stage-tag is-empty">${rows.length} without a room</span>
      </div>
      ${ctx.emailPlacementNotice ? `<div class="alert alert-info">${escapeHtml(ctx.emailPlacementNotice)}</div>` : ""}
      <div class="scroll-window">
        ${rowsHtml}
      </div>
      <div class="place-batch-footer">
        <span data-place-batch-count>0 selected</span>
        <button type="button" class="btn-sm btn-primary" data-place-selected disabled>Place selected</button>
      </div>
    </div>`;
}

/**
 * Advisory shown when the app cannot tell whose screen it is on — selfRole is
 * null until getUserContext is declared on the Marketplace listing. Buttons
 * stay live: guessing "not host" would lock the host out of their own round.
 */
const HOST_ONLY_NOTE = `These last two steps must be run by the <strong>meeting host</strong> — Zoom does not allow co-hosts to close rooms.`;

/** Shown once the role is known and this operator is a co-host, whose Close is refused with 10001. */
const CO_HOST_BLOCKED_NOTE = `Only the <strong>meeting host</strong> can close the round and move to the next one. Ask them to run these steps.`;

/**
 * Copy for the two host-only steps, and whether this operator may run them.
 *
 * Three cases on purpose: a known host sees nothing (the warning is noise to
 * them), a known co-host is blocked before Zoom can refuse, and an unknown role
 * gets the advisory with the buttons live.
 */
export function hostOnlyStepGuidance(
  role: OperatorRole | null | undefined,
): { note: string; blocked: boolean } {
  if (role === "host") return { note: "", blocked: false };
  // `== null` on purpose — undefined is just as unknown as null, and blocking on
  // a state object that simply has not been populated would lock the host out.
  if (role == null) return { note: HOST_ONLY_NOTE, blocked: false };
  return { note: CO_HOST_BLOCKED_NOTE, blocked: true };
}

function renderStageControls(
  plan: AssignmentPlan,
  state: AppState,
  hostNoteShownElsewhere: boolean,
): { stageBanner: string; actionButton: string } {
  switch (state.breakoutStage) {
    case "idle":
      // No pairs exist for this round yet — createBreakoutRooms with zero
      // rooms is a client-side dead end (Zoom has nothing to name), so offer
      // the same "Create Pairs" action admin_app runs instead of a button
      // that leads straight to an error. Full events only: a Lite plan comes
      // straight from the organiser's CSV, with no admin_app round to pair.
      if (plan.rooms.length === 0 && !state.isLite) {
        return {
          stageBanner: "",
          actionButton: `<button class="btn btn-primary" id="breakout-create-pairs-btn">Create Pairs</button>`,
        };
      }
      return {
        stageBanner: "",
        actionButton: `<button class="btn btn-primary" id="breakout-create-btn">Create Breakout Rooms</button>`,
      };
    case "created":
      // What just happened in Zoom now lives in the card's own sentence
      // (STAGE_CARD_META.created.sentence) — repeating it here as a second,
      // differently-coloured box was the duplication this stage used to have.
      return {
        stageBanner: "",
        actionButton: `
          <button class="btn btn-primary" id="breakout-assign-btn">Assign Participants</button>
          <button class="btn btn-secondary" id="breakout-discard-btn" style="margin-top:8px">Discard rooms</button>`,
      };
    case "assigned": {
      return {
        stageBanner: stageNotice(`Opening rooms <strong>moves everyone immediately.</strong>`, ""),
        actionButton: `
          <button class="btn btn-primary" id="breakout-open-btn">Open Rooms</button>
          <button class="btn btn-secondary" id="breakout-discard-btn" style="margin-top:8px">Discard rooms</button>`,
      };
    }
    case "opened": {
      // Steps 4 and 5 are host-only: Zoom returns 10001 to a co-host on both
      // closeBreakoutRooms and deleteBreakoutRoom, though it documents Co-Host
      // as supported on each.
      const guidance = hostOnlyStepGuidance(state.selfRole);
      return {
        stageBanner: stageNotice(
          `Closing <strong>ends the breakout for everyone and cannot be undone.</strong>`,
          hostNoteShownElsewhere ? "" : guidance.note,
        ),
        actionButton: guidance.blocked
          ? `<button class="btn btn-primary" id="breakout-close-btn" disabled title="Only the meeting host can close breakout rooms">Close Breakout Rooms</button>`
          : `<button class="btn btn-primary" id="breakout-close-btn">Close Breakout Rooms</button>`,
      };
    }
    case "closed":
      // The "round closed, moving on starts the next round" fact now lives in
      // the card's sentence — this banner is left only for the host-only
      // advisory, and disappears entirely once the operator is a known host.
      return {
        stageBanner: stageNotice("", hostNoteShownElsewhere ? "" : hostOnlyStepGuidance(state.selfRole).note),
        actionButton: "",
      };
    case "failed":
      return {
        stageBanner: `<div class="alert alert-error">Last step failed — see the message above. Use Back to retry.</div>`,
        actionButton: "",
      };
  }
}

export function bindBreakoutButtons(handlers: {
  onCreate: () => void;
  onCreatePairs: () => void;
  onAssign: () => void;
  onOpen: () => void;
  onClose: () => void;
  onDiscard: () => void;
}): void {
  document.getElementById("breakout-create-btn")?.addEventListener("click", handlers.onCreate);
  document.getElementById("breakout-create-pairs-btn")?.addEventListener("click", handlers.onCreatePairs);
  document.getElementById("breakout-assign-btn")?.addEventListener("click", handlers.onAssign);
  document.getElementById("breakout-open-btn")?.addEventListener("click", handlers.onOpen);
  document.getElementById("breakout-close-btn")?.addEventListener("click", handlers.onClose);
  document.getElementById("breakout-discard-btn")?.addEventListener("click", handlers.onDiscard);
}

/** Wires every combo rendered by planComboHtml() — type-to-filter, click-to-choose. */
function bindComboSearch(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>("[data-place-combo]").forEach((combo) => {
    const input = combo.querySelector<HTMLInputElement>("[data-place-search]");
    const hidden = combo.querySelector<HTMLInputElement>("[data-place-select]");
    const list = combo.querySelector<HTMLElement>("[data-place-list]");
    if (!input || !hidden || !list) return;
    const options = Array.from(list.querySelectorAll<HTMLElement>(".combo-option"));

    const filter = () => {
      const q = input.value.trim().toLowerCase();
      let anyVisible = false;
      for (const opt of options) {
        const match = !q || (opt.dataset.name ?? "").includes(q);
        opt.hidden = !match;
        if (match) anyVisible = true;
      }
      list.querySelector(".combo-empty")?.remove();
      if (!anyVisible) {
        const empty = document.createElement("div");
        empty.className = "combo-empty";
        empty.textContent = "No match";
        list.appendChild(empty);
      }
      list.hidden = false;
    };

    const choose = (opt: HTMLElement) => {
      // Full-event options carry a live uuid; Lite live-unplaced options
      // (planComboOptionsHtml) carry a plan attendee key plus their room — see
      // AC-5, which requires the shown room to update on selection.
      hidden.value = opt.dataset.uuid ?? opt.dataset.attendeeKey ?? "";
      input.value = opt.textContent ?? "";
      list.hidden = true;

      if (opt.dataset.roomKey !== undefined) {
        const roomKeyField = combo.parentElement?.querySelector<HTMLInputElement>("[data-place-room-key]");
        const roomText = combo.parentElement?.querySelector<HTMLElement>("[data-place-room]");
        if (roomKeyField) roomKeyField.value = opt.dataset.roomKey;
        if (roomText) roomText.textContent = opt.dataset.roomName ?? "";
      }
    };

    input.addEventListener("focus", filter);
    input.addEventListener("input", () => {
      hidden.value = "";
      const roomKeyField = combo.parentElement?.querySelector<HTMLInputElement>("[data-place-room-key]");
      const roomText = combo.parentElement?.querySelector<HTMLElement>("[data-place-room]");
      if (roomKeyField) roomKeyField.value = "";
      if (roomText) roomText.textContent = "";
      filter();
    });
    // mousedown, not click — fires before the input's blur handler below,
    // so the option is still in the DOM (not hidden by blur) when chosen.
    options.forEach((opt) => {
      opt.addEventListener("mousedown", (e) => {
        e.preventDefault();
        choose(opt);
      });
    });
    input.addEventListener("blur", () => {
      setTimeout(() => {
        list.hidden = true;
        if (!hidden.value) input.value = "";
      }, 150);
    });
  });
}

export function bindPlacementControls(handlers: {
  onPlace: (attendeeKey: string, roomKey: string, liveUuid: string) => void;
  onPlaceBatch?: (placements: Array<{ attendeeKey: string; roomKey: string; liveUuid: string }>) => void;
  onDraftChange?: (
    liveUuid: string,
    attendeeKey: string,
    roomKey: string,
    selected: boolean,
    searchText: string,
  ) => void;
  onReassign: (attendeeKey: string, fromRoomKey: string, toRoomKey: string, liveUuid: string) => void;
}): void {
  bindComboSearch(document);

  const syncBatchControls = () => {
    const bodies = Array.from(document.querySelectorAll<HTMLElement>("[data-place-body][data-live-uuid]"));
    const selected = bodies.filter((body) => body.querySelector<HTMLInputElement>("[data-place-batch-check]")?.checked);
    const allMappings = bodies.map((body) => ({
      liveUuid: body.dataset.liveUuid ?? "",
      attendeeKey: body.querySelector<HTMLInputElement>("[data-place-select]")?.value ?? "",
    }));
    const mappings = selected.map((body) => ({
      attendeeKey: body.querySelector<HTMLInputElement>("[data-place-select]")?.value ?? "",
      roomKey: body.querySelector<HTMLInputElement>("[data-place-room-key]")?.value ?? "",
      liveUuid: body.dataset.liveUuid ?? "",
    }));
    const uniqueAttendees = new Set(mappings.map((mapping) => mapping.attendeeKey));
    const valid = mappings.length > 0 &&
      mappings.every((mapping) => mapping.attendeeKey && mapping.roomKey && mapping.liveUuid) &&
      uniqueAttendees.size === mappings.length &&
      mappings.every((mapping) =>
        allMappings.filter((other) => other.attendeeKey === mapping.attendeeKey).length === 1,
      );
    const count = document.querySelector<HTMLElement>("[data-place-batch-count]");
    const button = document.querySelector<HTMLButtonElement>("[data-place-selected]");
    if (count) count.textContent = `${selected.length} selected`;
    if (button) {
      button.disabled = !valid;
      button.title = selected.length > 0 && !valid
        ? "Map every selected attendee and use each planned participant only once."
        : "";
    }
  };

  const notifyDraftChange = (body: HTMLElement) => {
    const liveUuid = body.dataset.liveUuid;
    if (!liveUuid) return;
    handlers.onDraftChange?.(
      liveUuid,
      body.querySelector<HTMLInputElement>("[data-place-select]")?.value ?? "",
      body.querySelector<HTMLInputElement>("[data-place-room-key]")?.value ?? "",
      body.querySelector<HTMLInputElement>("[data-place-batch-check]")?.checked ?? false,
      body.querySelector<HTMLInputElement>("[data-place-search]")?.value ?? "",
    );
    syncBatchControls();
  };

  document.querySelectorAll<HTMLElement>("[data-place-body]").forEach((body) => {
    const btn = body.querySelector<HTMLButtonElement>("[data-place-confirm]");
    if (!btn) return;

    // VE-023: a Lite row is the live attendee (fixed, in data-live-uuid) and the
    // combobox picks which plan participant/room they are. A full-event row is
    // the reverse — the plan participant is fixed and the combobox picks the
    // live uuid — so the two branches read the same fields in opposite roles.
    if (body.dataset.liveUuid !== undefined) {
      const liveUuid = body.dataset.liveUuid;
      const attendeeKeyField = body.querySelector<HTMLInputElement>("[data-place-select]");
      const roomKeyField = body.querySelector<HTMLInputElement>("[data-place-room-key]");
      const searchField = body.querySelector<HTMLInputElement>("[data-place-search]");
      const batchCheck = body.querySelector<HTMLInputElement>("[data-place-batch-check]");
      searchField?.addEventListener("input", () => notifyDraftChange(body));
      batchCheck?.addEventListener("change", () => notifyDraftChange(body));
      body.querySelectorAll<HTMLElement>(".combo-option").forEach((option) => {
        option.addEventListener("mousedown", () => notifyDraftChange(body));
      });
      btn.addEventListener("click", () => {
        const attendeeKey = attendeeKeyField?.value ?? "";
        const roomKey = roomKeyField?.value ?? "";
        if (!attendeeKey || !roomKey) return;
        btn.disabled = true;
        notifyDraftChange(body);
        handlers.onPlace(attendeeKey, roomKey, liveUuid);
      });
      return;
    }

    const hiddenSelect = body.querySelector<HTMLInputElement>("[data-place-select]");
    if (!hiddenSelect) return;
    btn.addEventListener("click", () => {
      const liveUuid = hiddenSelect.value;
      if (!liveUuid) return;
      const attendeeKey = body.dataset.attendeeKey ?? "";
      const roomKey = body.dataset.roomKey ?? "";
      handlers.onPlace(attendeeKey, roomKey, liveUuid);
    });
  });

  document.querySelector<HTMLButtonElement>("[data-place-selected]")?.addEventListener("click", () => {
    const placements = Array.from(
      document.querySelectorAll<HTMLElement>("[data-place-body][data-live-uuid]"),
    )
      .filter((body) => body.querySelector<HTMLInputElement>("[data-place-batch-check]")?.checked)
      .map((body) => ({
        attendeeKey: body.querySelector<HTMLInputElement>("[data-place-select]")?.value ?? "",
        roomKey: body.querySelector<HTMLInputElement>("[data-place-room-key]")?.value ?? "",
        liveUuid: body.dataset.liveUuid ?? "",
      }));
    const allDraftKeys = Array.from(
      document.querySelectorAll<HTMLElement>("[data-place-body][data-live-uuid]"),
    ).map((body) => body.querySelector<HTMLInputElement>("[data-place-select]")?.value ?? "");
    const keys = new Set(placements.map((placement) => placement.attendeeKey));
    if (
      placements.length === 0 ||
      placements.some((placement) => !placement.attendeeKey || !placement.roomKey || !placement.liveUuid) ||
      keys.size !== placements.length ||
      placements.some((placement) => allDraftKeys.filter((key) => key === placement.attendeeKey).length > 1)
    ) {
      return;
    }
    if (handlers.onPlaceBatch) {
      const button = document.querySelector<HTMLButtonElement>("[data-place-selected]");
      if (button) button.disabled = true;
      document.querySelectorAll<HTMLInputElement>("[data-place-search], [data-place-batch-check]").forEach((input) => {
        input.disabled = true;
      });
      document.querySelectorAll<HTMLButtonElement>("[data-place-confirm]").forEach((placeButton) => {
        placeButton.disabled = true;
      });
      handlers.onPlaceBatch(placements);
    }
  });
  syncBatchControls();

  // Accept potential match: fill the combobox fields and check the batch checkbox.
  document.querySelectorAll<HTMLButtonElement>("[data-accept-potential]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const row = btn.closest(".participant-row");
      const body = row?.nextElementSibling as HTMLElement | null;
      if (!body?.dataset.liveUuid) return;

      const searchInput = body.querySelector<HTMLInputElement>("[data-place-search]");
      const selectInput = body.querySelector<HTMLInputElement>("[data-place-select]");
      const roomKeyInput = body.querySelector<HTMLInputElement>("[data-place-room-key]");
      const roomChip = body.querySelector<HTMLElement>("[data-place-room]");
      const batchCheck = body.querySelector<HTMLInputElement>("[data-place-batch-check]");

      if (searchInput) searchInput.value = btn.dataset.pmDisplayName ?? "";
      if (selectInput) selectInput.value = btn.dataset.pmAttendeeKey ?? "";
      if (roomKeyInput) roomKeyInput.value = btn.dataset.pmRoomKey ?? "";
      if (roomChip) roomChip.textContent = btn.dataset.pmRoomName ?? "";
      if (batchCheck) batchCheck.checked = true;

      // Hide the hint after accepting
      btn.closest(".potential-match-hint")?.remove();

      notifyDraftChange(body);
    });
  });

  document.querySelectorAll<HTMLElement>("[data-reassign-body]").forEach((body) => {
    const btn = body.querySelector<HTMLButtonElement>("[data-reassign-confirm]");
    const roomSelect = body.querySelector<HTMLSelectElement>("[data-reassign-room]");
    const identitySelect = body.querySelector<HTMLSelectElement>("[data-reassign-identity]");
    if (!btn || !roomSelect || !identitySelect) return;
    btn.addEventListener("click", () => {
      const liveUuid = identitySelect.value;
      if (!liveUuid) return;
      const attendeeKey = body.dataset.attendeeKey ?? "";
      const fromRoomKey = body.dataset.fromRoomKey ?? "";
      const toRoomKey = roomSelect.value;
      handlers.onReassign(attendeeKey, fromRoomKey, toRoomKey, liveUuid);
    });
  });
}

export function bindRoomAccordion(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-room-toggle]").forEach((header) => {
    header.addEventListener("click", () => {
      const idx = header.dataset.roomToggle;
      const panel = document.querySelector<HTMLElement>(`[data-room-panel="${idx}"]`);
      if (!panel) return;
      const isHidden = panel.hasAttribute("hidden");
      panel.toggleAttribute("hidden", !isHidden);
      header.classList.toggle("expanded", isHidden);
    });
  });
}

/** Expand/collapse a collapsible card panel, scrolling it into view on open. */
function bindCollapseToggle(toggleSelector: string, panelSelector: string): void {
  const toggle = document.querySelector<HTMLButtonElement>(toggleSelector);
  const panel = document.querySelector<HTMLElement>(panelSelector);
  if (!toggle || !panel) return;
  toggle.addEventListener("click", () => {
    const opening = panel.hasAttribute("hidden");
    panel.toggleAttribute("hidden", !opening);
    toggle.classList.toggle("expanded", opening);
    toggle.setAttribute("aria-expanded", String(opening));
    if (opening) panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });
}

/** Expand/collapse the absentee panel and (VE-026) the unregistered-attendees panel. */
export function bindAbsentToggle(): void {
  bindCollapseToggle("[data-absent-toggle]", "[data-absent-panel]");
  bindCollapseToggle("[data-unregistered-toggle]", "[data-unregistered-panel]");
}
